import { useEditor, EditorContent } from '@tiptap/react';
import { useEffect, useState } from 'react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Bold, Italic, List, ListOrdered, FileText, Highlighter } from 'lucide-react';
import './EditorRiche.css';

export function EditorRiche({ value, onChange, onChoixTemplate, onSauvegarderTemplate, placeholder, minHeight = '250px' }) {
  // Forcer le re-render de la toolbar quand la sélection change
  const [, forceUpdate] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit,
      // 🆕 2026-05-08 — Surligneur jaune (multicolore configurable)
      Highlight.configure({ multicolor: false }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onSelectionUpdate: () => {
      forceUpdate(n => n + 1);
    },
    onTransaction: () => {
      forceUpdate(n => n + 1);
    },
  });

  // Mettre à jour l'éditeur quand value change (templates)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  if (!editor) {
    return <div>Chargement...</div>;
  }

  const isBold = editor.isActive('bold');
  const isItalic = editor.isActive('italic');
  const isBullet = editor.isActive('bulletList');
  const isOrdered = editor.isActive('orderedList');
  const isHighlight = editor.isActive('highlight');

  const headingValue =
    editor.isActive('heading', { level: 1 }) ? 'h1' :
    editor.isActive('heading', { level: 2 }) ? 'h2' :
    editor.isActive('heading', { level: 3 }) ? 'h3' : 'p';

  const toolbarBtn = (active) => ({
    backgroundColor: active ? '#1f2937' : '#f3f4f6',
    color: active ? 'white' : '#374151',
    border: '1px solid #d1d5db',
  });

  return (
    <div className="space-y-2">
      {/* Toolbar */}
      <div className="flex gap-2 flex-wrap border-b pb-2 bg-gray-50 p-2 rounded-t items-center">
        {onChoixTemplate && (
          <Button
            type="button"
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onMouseDown={(e) => { e.preventDefault(); onChoixTemplate(); }}
          >
            <FileText className="w-4 h-4 mr-2" />
            Choisir parmi les modèles
          </Button>
        )}
        {onSauvegarderTemplate && (
          <Button
            type="button"
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onMouseDown={(e) => { e.preventDefault(); onSauvegarderTemplate(); }}
          >
            Sauvegarder comme modèle
          </Button>
        )}

        {/* Sélecteur Taille */}
        <Select
          value={headingValue}
          onValueChange={(val) => {
            if (val === 'p') editor.chain().focus().setParagraph().run();
            else if (val === 'h1') editor.chain().focus().toggleHeading({ level: 1 }).run();
            else if (val === 'h2') editor.chain().focus().toggleHeading({ level: 2 }).run();
            else if (val === 'h3') editor.chain().focus().toggleHeading({ level: 3 }).run();
          }}
        >
          <SelectTrigger className="w-32 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="p">Normal</SelectItem>
            <SelectItem value="h1">Titre 1</SelectItem>
            <SelectItem value="h2">Titre 2</SelectItem>
            <SelectItem value="h3">Titre 3</SelectItem>
          </SelectContent>
        </Select>

        <div className="border-l border-gray-300 pl-2 ml-2 flex gap-1">
          {/* Gras — reste actif visuellement tant que le curseur est en gras */}
          <Button
            type="button"
            size="sm"
            title="Gras (Ctrl+B)"
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
            style={toolbarBtn(isBold)}
          >
            <Bold className="w-4 h-4" />
          </Button>

          <Button
            type="button"
            size="sm"
            title="Italique (Ctrl+I)"
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
            style={toolbarBtn(isItalic)}
          >
            <Italic className="w-4 h-4" />
          </Button>

          <Button
            type="button"
            size="sm"
            title="Liste à puces"
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}
            style={toolbarBtn(isBullet)}
          >
            <List className="w-4 h-4" />
          </Button>

          <Button
            type="button"
            size="sm"
            title="Liste numérotée"
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}
            style={toolbarBtn(isOrdered)}
          >
            <ListOrdered className="w-4 h-4" />
          </Button>

          {/* 🆕 2026-05-08 — Surligneur jaune */}
          <Button
            type="button"
            size="sm"
            title="Surligner (jaune)"
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHighlight().run(); }}
            style={toolbarBtn(isHighlight)}
          >
            <Highlighter className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Zone d'édition — clic n'importe où pour commencer à écrire */}
      <div
        className="editor-container border rounded-md bg-white p-4 cursor-text"
        style={{ minHeight }}
        onClick={() => editor.commands.focus()}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
